"use client";

import { useEffect, useRef, useState } from "react";
import { ArrowRight, MoveHorizontal } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function PerformanceComparison() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: compRef,
    isVisible: compVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const [sliderPos, setSliderPos] = useState(50);
  const [isSnapping, setIsSnapping] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef(false);
  const beforeItems = ["LOW DIRECT BOOKINGS", "HIGH OTA COMMISSIONS", "STAGNANT REVENUE", "POOR RATE PARITY"];
  const afterItems = ["MAXIMIZED DIRECT SALES", "OPTIMIZED OTA MARGINS", "SCALABLE REVENUE GROWTH", "PRECISE RATE CONTROL"];
  const updateFromClientX = (clientX: number) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = clientX - rect.left;
    const pct = Math.max(0, Math.min(100, x / rect.width * 100));
    setSliderPos(pct);
  };
  useEffect(() => {
    const handleMove = (e: MouseEvent | TouchEvent) => {
      if (!draggingRef.current) return;
      const clientX = "touches" in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
      updateFromClientX(clientX);
    };
    const handleUp = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      setIsSnapping(true);
      window.setTimeout(() => setIsSnapping(false), 220);
    };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    window.addEventListener("touchmove", handleMove, {
      passive: true
    });
    window.addEventListener("touchend", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", handleUp);
    };
  }, []);
  const startDrag = (clientX: number) => {
    draggingRef.current = true;
    setIsSnapping(false);
    updateFromClientX(clientX);
  };
  const [flippedBefore, setFlippedBefore] = useState(false);
  const [flippedAfter, setFlippedAfter] = useState(false);
  return <section data-section-id="1897" id="performancecomparison" className="relative bg-slate-50 text-slate-900 overflow-hidden py-28 md:py-40" style={{
    clipPath: "polygon(0 6vw, 100% 0, 100% 100%, 0 100%)"
  }}>
      {}
      <div className="relative max-w-[1360px] mx-auto px-5 md:px-8">
        <div ref={headerRef} className="relative mx-auto max-w-[980px] text-center" style={{
        transform: headerVisible ? "translateY(0)" : "translateY(24px)",
        opacity: headerVisible ? 1 : 0,
        transition: "transform 520ms cubic-bezier(0.2,0.8,0.2,1), opacity 520ms cubic-bezier(0.2,0.8,0.2,1)"
      }}>
          {}
          <div aria-hidden className="pointer-events-none absolute -top-[8%] -right-[10%] select-none font-default font-extrabold leading-none" style={{
          fontSize: "22rem",
          color: "transparent",
          WebkitTextStroke: "1px hsl(222 47% 7% / 0.10)",
          lineHeight: 0.8
        }}>
            02
          </div>

          {}
          <div className="relative flex items-center justify-center gap-3 font-default uppercase" style={{
          letterSpacing: "0.22em"
        }}>
            <span className="text-blue-600 text-xs md:text-sm">§</span>
            <span className="text-slate-600 text-[11px] md:text-[13px]">02 / REVENUE TRANSFORMATION</span>
          </div>

          {}
          <div aria-hidden className="mx-auto mt-6 h-[4px] bg-blue-600" style={{
          width: "48px",
          transformOrigin: "left",
          transform: headerVisible ? "scaleX(1)" : "scaleX(0)",
          transition: "transform 280ms cubic-bezier(0.2,0.8,0.2,1) 200ms"
        }} />

          {}
          <h2 className="relative mt-8 font-default font-extrabold uppercase text-slate-900" style={{
          fontSize: "clamp(3rem, 7.5vw, 7rem)",
          lineHeight: 0.88,
          letterSpacing: "-0.005em"
        }}>
            FROM OTA DEPENDENCY TO{" "}
            <em className="not-italic md:italic text-blue-600" style={{
            fontStyle: "italic"
          }}>
              DIRECT
            </em>{" "}
            PROFITABILITY
          </h2>
        </div>
      </div>

      {}
      <div className="relative max-w-[1360px] mx-auto px-5 md:px-8 mt-16 md:mt-24">
        <div ref={compRef} style={{
        transform: compVisible ? "translateY(0)" : "translateY(24px)",
        opacity: compVisible ? 1 : 0,
        transition: "transform 520ms cubic-bezier(0.2,0.8,0.2,1) 120ms, opacity 520ms cubic-bezier(0.2,0.8,0.2,1) 120ms"
      }}>
          {}
          <div ref={containerRef} className="relative hidden md:block w-full overflow-hidden select-none border border-slate-200 rounded-lg shadow-lg" style={{
          aspectRatio: "21/10"
        }} onMouseDown={e => startDrag(e.clientX)} onTouchStart={e => startDrag(e.touches[0].clientX)}>
            {}
            <div className="absolute inset-0">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/optimized_revenue_growth.png" alt="After: optimized hotel revenue growth and direct bookings" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
              {}
              <div className="absolute top-8 right-8 pointer-events-none">
                <div className="font-default uppercase text-[11px] tracking-[0.22em] text-white text-right">
                  After
                </div>
                <div className="font-default font-extrabold uppercase text-blue-600 leading-none text-right" style={{
                fontSize: "clamp(4rem, 11vw, 11rem)"
              }}>
                  AFTER
                </div>
              </div>
              {}
              <div className="absolute bottom-8 right-8 pointer-events-none">
                <div className="font-default uppercase text-[11px] tracking-[0.20em] space-y-2 text-right text-white">
                  {afterItems.map((item, i) => <div key={item} data-index={i} className="flex items-center justify-end gap-3">
                      <span>{item}</span>
                      <span className="inline-block h-[6px] w-[6px] bg-blue-600" />
                    </div>)}
                </div>
              </div>
            </div>

            {}
            <div className="absolute inset-0 overflow-hidden" style={{
            clipPath: `polygon(0 0, ${sliderPos}% 0, ${sliderPos}% 100%, 0 100%)`,
            transition: isSnapping ? "clip-path 200ms cubic-bezier(0.2,0.8,0.2,1)" : "none"
          }}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/stagnant_ota_dependency.png" alt="Before: stagnant revenue and high OTA dependency" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" style={{
              filter: "grayscale(100%) contrast(1.05)"
            }} />
              {}
              <div className="absolute top-8 left-8 pointer-events-none">
                <div className="font-default uppercase text-[11px] tracking-[0.22em] text-slate-900">
                  Before
                </div>
                <div className="font-default font-extrabold uppercase leading-none" style={{
                fontSize: "clamp(4rem, 11vw, 11rem)",
                color: "transparent",
                WebkitTextStroke: "1.5px hsl(222 47% 7%)"
              }}>
                  BEFORE
                </div>
              </div>
              {}
              <div className="absolute bottom-8 left-8 pointer-events-none">
                <div className="font-default uppercase text-[11px] tracking-[0.20em] space-y-2 text-slate-900">
                  {beforeItems.map((item, i) => <div key={item} data-index={i} className="flex items-center gap-3">
                      <span className="inline-block h-[6px] w-[6px] bg-slate-900" />
                      <span>{item}</span>
                    </div>)}
                </div>
              </div>
            </div>

            {}
            <div className="absolute top-0 bottom-0 pointer-events-none" style={{
            left: `${sliderPos}%`,
            transform: "translateX(-50%)",
            transition: isSnapping ? "left 200ms cubic-bezier(0.2,0.8,0.2,1)" : "none"
          }}>
              <div className="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-[4px] bg-blue-600" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-blue-600 flex items-center justify-center text-white rounded-lg" style={{
              boxShadow: "var(--shadow-lg)"
            }}>
                <MoveHorizontal className="w-5 h-5" strokeWidth={1.5} />
              </div>
            </div>

            {}
            <div className="absolute top-3 left-1/2 -translate-x-1/2 font-default uppercase text-[10px] tracking-[0.22em] text-slate-600 pointer-events-none">
              Drag To Compare
            </div>
          </div>

          {}
          <div className="md:hidden grid grid-cols-1 gap-4">
            {}
            <button type="button" onClick={() => setFlippedBefore(!flippedBefore)} className="relative w-full overflow-hidden border border-slate-200 rounded-lg text-left" style={{
            aspectRatio: "4/5"
          }} aria-label="Tap to flip before tile">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/stagnant_ota_dependency.png" alt="Before: stagnant revenue and high OTA dependency" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" style={{
              filter: flippedBefore ? "none" : "grayscale(100%) contrast(1.05)",
              transition: "filter 220ms"
            }} />
              <div className="absolute inset-0 bg-slate-50/30" />
              <div className="absolute top-5 left-5">
                <div className="font-default uppercase text-[10px] tracking-[0.22em] text-slate-900">Before</div>
                <div className="font-default font-extrabold uppercase leading-none mt-1" style={{
                fontSize: "4.5rem",
                color: "transparent",
                WebkitTextStroke: "1.5px hsl(222 47% 7%)"
              }}>
                  BEFORE
                </div>
              </div>
              <div className="absolute bottom-5 left-5 right-5">
                <div className="font-default uppercase text-[10px] tracking-[0.20em] space-y-1.5 text-slate-900">
                  {beforeItems.map((item, i) => <div key={item} data-index={i} className="flex items-center gap-2">
                      <span className="inline-block h-[5px] w-[5px] bg-slate-900" />
                      <span>{item}</span>
                    </div>)}
                </div>
              </div>
              <div className="absolute top-3 right-3 font-mono uppercase text-[9px] tracking-[0.20em] text-slate-600">
                Tap To Flip
              </div>
            </button>

            {}
            <button type="button" onClick={() => setFlippedAfter(!flippedAfter)} className="relative w-full overflow-hidden border border-slate-200 rounded-lg text-left" style={{
            aspectRatio: "4/5"
          }} aria-label="Tap to flip after tile">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/optimized_revenue_growth.png" alt="After: optimized hotel revenue growth and direct bookings" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" style={{
              filter: flippedAfter ? "grayscale(100%) contrast(1.05)" : "none",
              transition: "filter 220ms"
            }} />
              <div className="absolute inset-0" style={{
              background: "linear-gradient(180deg, hsl(222 47% 7% / 0.2), hsl(222 47% 7% / 0.5))"
            }} />
              <div className="absolute top-5 right-5 text-right">
                <div className="font-default uppercase text-[10px] tracking-[0.22em] text-white">After</div>
                <div className="font-default font-extrabold uppercase leading-none mt-1 text-blue-600" style={{
                fontSize: "4.5rem"
              }}>
                  AFTER
                </div>
              </div>
              <div className="absolute bottom-5 right-5 left-5">
                <div className="font-default uppercase text-[10px] tracking-[0.20em] space-y-1.5 text-right text-white">
                  {afterItems.map((item, i) => <div key={item} data-index={i} className="flex items-center justify-end gap-2">
                      <span>{item}</span>
                      <span className="inline-block h-[5px] w-[5px] bg-blue-600" />
                    </div>)}
                </div>
              </div>
              <div className="absolute top-3 left-3 font-mono uppercase text-[9px] tracking-[0.20em] text-white/80">
                Tap To Flip
              </div>
            </button>
          </div>
        </div>

        {}
        <div className="mt-12 md:mt-16 flex justify-center">
          <Link to="/OurServicesPage" className="group inline-flex items-center gap-3 font-default uppercase text-[12px] md:text-[13px] tracking-[0.22em] text-blue-600">
            <span className="relative">
              DISCOVER OUR REVENUE STRATEGIES
              <span aria-hidden className="absolute left-0 right-0 -bottom-1.5 h-[2px] bg-blue-600" style={{
              width: "calc(100% + 16px)",
              marginLeft: "-8px"
            }} />
            </span>
            <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1.5" strokeWidth={1.5} />
          </Link>
        </div>
      </div>
    </section>;
}